import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import API_BASE from '../../utils/apiBase';
import { currentPublicBrand } from '../../lib/publicBrand';
import CareDockProspectForm from '../../components/public/CareDockProspectForm';

const API = API_BASE;

export default function ContactPage() {
  const brand = currentPublicBrand();
  const [searchParams] = useSearchParams();
  const initialEnquiryType = searchParams.get('type') === 'platform_demo' ? 'platform_demo' : 'platform_demo';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [prospectReady, setProspectReady] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    organisation: '',
    enquiry_type: initialEnquiryType,
    subject: `${brand.isCareDock ? 'CareDock' : 'CareOps'} demo / subscription access`,
    message: '',
    website: '',
  });

  const handleEnquiryTypeChange = (e) => {
    const enquiry_type = e.target.value;
    const subjectDefaults = {
      platform_demo: `${brand.isCareDock ? 'CareDock' : 'CareOps'} demo / subscription access`,
      other: `${brand.isCareDock ? 'CareDock' : 'CareOps'} enquiry`,
    };
    setFormData((prev) => ({
      ...prev,
      enquiry_type,
      subject: prev.subject.trim() ? prev.subject : (subjectDefaults[enquiry_type] || ''),
    }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you. We will be in touch soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // This public, read-only readiness call never retrieves customer records.
  // CareDock's non-collecting holding page remains the default on errors,
  // until an independently approved platform notice and intake are published.
  useEffect(() => {
    if (!brand.isCareDock) return undefined;
    let active = true;
    axios.get(`${API}/platform/prospects/readiness`)
      .then(result => {
        if (active && result?.data?.accepting_enquiries === true &&
            /^https:\/\//i.test(result.data.privacy_notice_url || '') &&
            result.data.privacy_notice_version) {
          setProspectReady(result.data);
        } else if (active) {
          setProspectReady(null);
        }
      })
      .catch(() => { if (active) setProspectReady(null); });
    return () => { active = false; };
  }, [brand.isCareDock]);

  // Platform enquiries must never enter a care-provider tenant's inbox.
  // Do not collect prospect data until CareDock owns verified contact,
  // privacy, consent and platform-admin routing end to end.
  if (brand.isCareDock) {
    return (
      <div className="caredock-platform-theme flex min-h-screen flex-col bg-[#F3FAFF] text-[#0D274E]" data-testid="caredock-contact-gate">
        <Header minimal />
        <main className="flex flex-1 items-center justify-center px-5 py-20">
          <section className="w-full max-w-2xl rounded-[22px] border border-[#dce8f0] bg-white p-8 shadow-sm sm:p-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-[#0083bd]">CareDock platform</p>
            <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Provider enquiries</h1>
            {prospectReady ? (
              <CareDockProspectForm
                noticeUrl={prospectReady.privacy_notice_url}
                noticeVersion={prospectReady.privacy_notice_version}
              />
            ) : (
              <>
                <p className="mt-5 text-base leading-7 text-[#51677a]">
                  CareDock is preparing its own provider-enquiry service. We are not accepting new enquiries on this page yet.
                </p>
                <p className="mt-3 text-sm leading-6 text-[#667b8d]">
                  If you already have an invitation from your organisation, you can continue to your secure workspace.
                </p>
              </>
            )}
            <Link to="/access" className="mt-8 inline-flex min-h-11 items-center rounded-xl bg-[#0D274E] px-6 py-3 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#008fd5]">
              Access your workspace
            </Link>
          </section>
        </main>
        <Footer minimal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      <section className="bg-white py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-6">
              Request {brand.isCareDock ? 'CareDock' : 'CareOps'} access
            </h1>
            <p className="text-lg text-text-muted leading-relaxed">
              {brand.isCareDock ? 'CareDock is invite-only while new provider workspaces and subscriptions are being introduced.' : 'CareOps is invite-only while workplace setup, subscription plans and tenant controls are managed by the platform owner.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-3 bg-white border-b border-[#E4E8EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-text-muted text-center">
            Already invited?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="contact-route-login">
              Go to workplace login.
            </Link>
          </p>
        </div>
      </section>

      <section className="pt-12 pb-20 lg:pt-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="space-y-8">
              {brand.isCareDock && <p className="text-xs leading-5 text-text-muted">Enquiries currently use the Osabea Healthcare Solutions Ltd contact team.</p>}
              <div className="card-public">
                <Phone className="h-6 w-6 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-text-primary mb-2">Call us</h3>
                <a href="tel:01622809469" className="text-text-muted hover:text-primary transition-colors block">
                  01622 809469
                </a>
              </div>

              <div className="card-public">
                <Mail className="h-6 w-6 text-primary mb-4" />
                <h3 className="font-heading font-semibold text-text-primary mb-2">Email us</h3>
                <a href="mailto:info@osabeacares.co.uk" className="text-text-muted hover:text-primary transition-colors">
                  info@osabeacares.co.uk
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl border border-[#E4E8EB] p-8 lg:p-12">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-support-accent rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-8 w-8 text-success" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-text-primary mb-4">
                      Thank you
                    </h3>
                    <p className="text-text-muted">
                      We will review your workplace request and get back to you.
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-2xl font-semibold text-text-primary mb-6">
                      Request an invite
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="full_name">Full name *</Label>
                          <Input
                            id="full_name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            required
                            className="rounded-xl border-[#E4E8EB]"
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="rounded-xl border-[#E4E8EB]"
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone number</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            className="rounded-xl border-[#E4E8EB]"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="organisation">Workplace / organisation name *</Label>
                          <Input
                            id="organisation"
                            name="organisation"
                            value={formData.organisation}
                            onChange={handleChange}
                            required
                            className="rounded-xl border-[#E4E8EB]"
                            data-testid="contact-org"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="enquiry_type">What is your enquiry about? *</Label>
                        <select
                          id="enquiry_type"
                          name="enquiry_type"
                          value={formData.enquiry_type}
                          onChange={handleEnquiryTypeChange}
                          required
                          className="w-full px-3 py-2 rounded-xl border border-[#E4E8EB] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="contact-enquiry-type"
                        >
                          <option value="platform_demo">{brand.isCareDock ? "CareDock demo or subscription access" : "CareOps demo or subscription access"}</option>
                          <option value="other">Something else</option>
                        </select>
                        <p className="text-xs text-text-muted">
                          Tell us your team size, the workflow you want to see first, and whether this is for one site or multiple locations.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="rounded-xl border-[#E4E8EB]"
                          data-testid="contact-subject"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="rounded-xl border-[#E4E8EB]"
                          data-testid="contact-message"
                        />
                      </div>

                      <p className="text-sm text-text-muted">
                        Do not include sensitive personal, medical or applicant information in this form.
                      </p>
                      <p className="text-sm text-text-muted">
                        By submitting this form, you agree that Osabea may contact you about your enquiry. For more details, see our{' '}
                        <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                      </p>

                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                      />

                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="consent"
                          checked={consent}
                          onChange={(e) => setConsent(e.target.checked)}
                          required
                          className="mt-1 h-4 w-4 rounded border-[#E4E8EB] text-primary"
                          data-testid="contact-consent"
                        />
                        <label htmlFor="consent" className="text-sm text-text-muted">
                          I agree that Osabea may contact me about my enquiry. *
                        </label>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting || !consent}
                        className="bg-primary hover:bg-primary-hover text-white rounded-full px-8"
                        data-testid="contact-submit"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Request invite'
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
